/**
 * 住所を変えて保存するとき、手で直したピンがあれば聞く（#132、docs/49 §2.3）。地図（MapLibre／Google）とは別のファイル＝案件の画面が先に読む小さい部品。
 * ピンの位置の住所を入れるかを聞く窓（#135・#136）もここ＝地理院の地図（SiteMap）と Google の地図（GoogleSiteMap）が同じ窓を使う
 */
import { GROUP_MSG, type SiteGeoReverse } from "@genbaryoku/contract";
import { useEffect, useState } from "react";

export function SiteGeoFollowDialog({ onAnswer }: { onAnswer: (follow: boolean) => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onAnswer(false);
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onAnswer]);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onAnswer(false)}>
      <div className="dlg" role="alertdialog" aria-label={GROUP_MSG.geoFollowAsk} aria-describedby="geo-follow-desc" data-testid="geo-follow">
        <div className="dh">{GROUP_MSG.geoFollowAsk}</div>
        <div className="db">
          <p id="geo-follow-desc" style={{ margin: 0 }}>
            手で修正したピンがあります。「ピン移動」で新しい住所の位置に置き直し、「そのまま」で今のピンを残します。
          </p>
        </div>
        <div className="df">
          <button type="button" className="btn primary" autoFocus onClick={() => onAnswer(true)} data-testid="geo-follow-yes">
            ピン移動
          </button>
          <button type="button" className="btn" onClick={() => onAnswer(false)} data-testid="geo-follow-no">
            そのまま
          </button>
        </div>
      </div>
    </div>
  );
}

/** 引いた住所の細かさ（窓の添え書き） */
const REVERSE_LEVEL: Partial<Record<string, string>> = { residence: "号まで", parcel: "地番まで", block: "街区まで", town: "町名まで" };

/**
 * ピンの位置の住所を現場住所に入れるかを聞く窓（#135）。今の現場住所と並べて見せる。
 * 住所の欄は直せる（#136：町名までのとき＝地番の位置が公開されていない町＝番地をここで足す）。onAnswer(住所)＝入れる、onAnswer(null)＝入れない
 */
export function SiteGeoReverseDialog({ r, current, onAnswer }: { r: SiteGeoReverse; current: string; onAnswer: (address: string | null) => void }) {
  const [address, setAddress] = useState(r.address ?? "");
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onAnswer(null);
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onAnswer]);
  const note = [r.level ? REVERSE_LEVEL[r.level] : "", r.distanceM != null ? `ピンから約 ${r.distanceM}m` : ""].filter(Boolean).join("・");
  const ok = address.trim().length > 0;
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onAnswer(null)}>
      <div className="dlg" role="alertdialog" aria-label={GROUP_MSG.geoReverseAsk} aria-describedby="geo-reverse-desc" data-testid="geo-reverse">
        <div className="dh">{GROUP_MSG.geoReverseAsk}</div>
        <div className="db">
          <div className="fld">
            <label className="lbl" htmlFor="geo-reverse-address">ピンの位置の住所{note ? `（${note}）` : ""}</label>
            <input id="geo-reverse-address" className="inp" value={address} onChange={(e) => setAddress(e.target.value)} autoFocus data-testid="geo-reverse-address" />
          </div>
          {r.townOnly ? (
            <div className="next warn" id="geo-reverse-desc" data-testid="geo-reverse-town-only">
              <span>{GROUP_MSG.geoTownOnly}</span>
            </div>
          ) : null}
          <dl className="kv" style={{ margin: 0 }}>
            <dt>今の現場住所</dt>
            <dd>{current || <span className="muted">—</span>}</dd>
          </dl>
        </div>
        <div className="df">
          <button type="button" className="btn primary" disabled={!ok} onClick={() => onAnswer(address.trim())} data-testid="geo-reverse-yes">
            住所に反映
          </button>
          <button type="button" className="btn" onClick={() => onAnswer(null)} data-testid="geo-reverse-no">
            そのまま
          </button>
        </div>
      </div>
    </div>
  );
}
